import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import { useAdminToast } from '../../hooks/useAdminToast';
import { 
  Users, DollarSign, Clock, CheckCircle, X, 
  AlertCircle, User, ArrowRight, RefreshCw, UserPlus
} from 'lucide-react';

const SupportDashboard = () => {
  const { toast } = useAdminToast();
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [newClients, setNewClients] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [assignAdminId, setAssignAdminId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('deposits'); // deposits, clients

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [depositsRes, clientsRes, adminsRes] = await Promise.all([
        adminAPI.getPendingFirstDeposits(),
        adminAPI.getNewClientsForSupport(),
        adminAPI.getAdminsListForSupport()
      ]);
      
      setPendingDeposits(depositsRes.data || []);
      setNewClients(clientsRes.data || []);
      setAdmins(adminsRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDeposit = async () => {
    if (!selectedDeposit || !assignAdminId) return;
    
    setProcessing(true);
    try {
      await adminAPI.supportApproveFirstDeposit(
        selectedDeposit.deposit.id,
        assignAdminId,
        selectedDeposit.deposit.amount
      );
      
      setSelectedDeposit(null);
      setAssignAdminId('');
      loadData();
    } catch (err) {
      console.error('Failed to approve:', err);
      toast.apiError(err, 'Ошибка при подтверждении');
    } finally {
      setProcessing(false);
    }
  };

  const handleAssignClient = async () => {
    if (!selectedClient || !assignAdminId) return;
    
    setProcessing(true);
    try {
      await adminAPI.supportAssignClient(selectedClient.user.id, assignAdminId);
      setSelectedClient(null);
      setAssignAdminId('');
      loadData();
    } catch (err) {
      console.error('Failed to assign:', err);
      toast.apiError(err, 'Ошибка при назначении');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Панель Support
          </h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Обработка новых клиентов и первых депозитов
          </p>
        </div>
        <button
          onClick={loadData}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <RefreshCw size={16} />
          Обновить
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <StatCard 
          icon={Users} 
          label="Новые клиенты" 
          value={newClients.length}
          color="#3b82f6"
        />
        <StatCard 
          icon={Clock} 
          label="Ожидают депозиты" 
          value={pendingDeposits.length}
          color={COLORS.primary}
        />
        <StatCard 
          icon={UserPlus} 
          label="Без депозитов" 
          value={newClients.filter(c => !c.has_approved_deposits && c.pending_deposits === 0).length}
          color="#8b5cf6"
        />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('deposits')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'deposits' ? COLORS.primary : COLORS.background,
            border: `1px solid ${activeTab === 'deposits' ? COLORS.primary : COLORS.border}`,
            borderRadius: '8px',
            color: activeTab === 'deposits' ? '#000' : COLORS.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Pending депозиты ({pendingDeposits.length})
        </button>
        <button
          onClick={() => setActiveTab('clients')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'clients' ? COLORS.primary : COLORS.background,
            border: `1px solid ${activeTab === 'clients' ? COLORS.primary : COLORS.border}`,
            borderRadius: '8px',
            color: activeTab === 'clients' ? '#000' : COLORS.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Все новые клиенты ({newClients.length})
        </button>
      </div>

      {/* Content */}
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
            Загрузка...
          </div>
        ) : activeTab === 'deposits' ? (
          /* Pending Deposits Tab */
          pendingDeposits.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <CheckCircle size={48} color={COLORS.success} style={{ marginBottom: '16px' }} />
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                Нет ожидающих депозитов
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                Все первые депозиты обработаны
              </div>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 100px 140px',
                padding: '12px 20px',
                backgroundColor: COLORS.background,
                borderBottom: `1px solid ${COLORS.border}`,
                gap: '16px'
              }}>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>КЛИЕНТ</span>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>СУММА</span>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>СТАТУС</span>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ДЕЙСТВИЕ</span>
              </div>
              {pendingDeposits.map((item) => (
                <div
                  key={item.deposit.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 100px 140px',
                    padding: '14px 20px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: `${COLORS.primary}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={20} color={COLORS.primary} />
                    </div>
                    <div>
                      <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
                        {item.user?.name || 'Новый клиент'}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        {item.user?.email}
                      </div>
                    </div>
                  </div>
                  <div style={{ color: COLORS.success, fontSize: '16px', fontWeight: '700' }}>
                    +${item.deposit.amount?.toFixed(2)}
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: `${COLORS.primary}20`,
                    color: COLORS.primary,
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    Первый депозит
                  </span>
                  <button
                    onClick={() => setSelectedDeposit(item)}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: COLORS.success,
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      justifyContent: 'center'
                    }}
                  >
                    Подтвердить
                    <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          /* All New Clients Tab */
          newClients.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <CheckCircle size={48} color={COLORS.success} style={{ marginBottom: '16px' }} />
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '500' }}>
                Нет новых клиентов
              </div>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 140px',
                padding: '12px 20px',
                backgroundColor: COLORS.background,
                borderBottom: `1px solid ${COLORS.border}`,
                gap: '16px'
              }}>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>КЛИЕНТ</span>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>БАЛАНС</span>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ДЕПОЗИТЫ</span>
                <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ДЕЙСТВИЕ</span>
              </div>
              {newClients.map((item) => (
                <div
                  key={item.user.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 140px',
                    padding: '14px 20px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: `#3b82f620`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={20} color="#3b82f6" />
                    </div>
                    <div>
                      <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
                        {item.user?.name || item.user?.email?.split('@')[0]}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        {item.user?.email}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                        {formatDate(item.user?.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600' }}>
                    ${item.balance?.exchange_balance?.toFixed(2) || '0.00'}
                  </div>
                  <div>
                    {item.pending_deposits > 0 ? (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        backgroundColor: `${COLORS.primary}20`,
                        color: COLORS.primary,
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {item.pending_deposits} pending
                      </span>
                    ) : item.has_approved_deposits ? (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        backgroundColor: `${COLORS.success}20`,
                        color: COLORS.success,
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        Есть депозиты
                      </span>
                    ) : (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        backgroundColor: `${COLORS.textSecondary}20`,
                        color: COLORS.textSecondary,
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        Нет депозитов
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedClient(item)}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      color: COLORS.text,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Назначить
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Approve Deposit Modal */}
      {selectedDeposit && (
        <ApproveModal
          title="Подтверждение депозита"
          item={selectedDeposit}
          admins={admins}
          assignAdminId={assignAdminId}
          setAssignAdminId={setAssignAdminId}
          onClose={() => { setSelectedDeposit(null); setAssignAdminId(''); }}
          onConfirm={handleApproveDeposit}
          processing={processing}
          isDeposit={true}
        />
      )}

      {/* Assign Client Modal */}
      {selectedClient && (
        <ApproveModal
          title="Назначить клиента"
          item={selectedClient}
          admins={admins}
          assignAdminId={assignAdminId}
          setAssignAdminId={setAssignAdminId}
          onClose={() => { setSelectedClient(null); setAssignAdminId(''); }}
          onConfirm={handleAssignClient}
          processing={processing}
          isDeposit={false}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div style={{
    backgroundColor: COLORS.card,
    borderRadius: '12px',
    padding: '20px',
    border: `1px solid ${COLORS.border}`
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        backgroundColor: `${color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon size={22} color={color} />
      </div>
      <div>
        <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '4px' }}>
          {label}
        </div>
        <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

// Approve/Assign Modal Component
const ApproveModal = ({ title, item, admins, assignAdminId, setAssignAdminId, onClose, onConfirm, processing, isDeposit }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }} onClick={onClose}>
    <div 
      style={{
        backgroundColor: COLORS.card,
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '450px',
        margin: '20px'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
          {title}
        </h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
          <X size={20} />
        </button>
      </div>

      {/* Client Info */}
      <div style={{
        backgroundColor: COLORS.background,
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: `${COLORS.primary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <User size={24} color={COLORS.primary} />
          </div>
          <div>
            <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600' }}>
              {item.user?.name || 'Новый клиент'}
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
              {item.user?.email}
            </div>
          </div>
        </div>
        
        {isDeposit && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '12px',
            paddingTop: '12px',
            borderTop: `1px solid ${COLORS.border}`
          }}>
            <div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                Сумма депозита
              </div>
              <div style={{ color: COLORS.success, fontSize: '20px', fontWeight: '700' }}>
                ${item.deposit?.amount?.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                Текущий баланс
              </div>
              <div style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700' }}>
                ${item.balance?.exchange_balance?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Select Admin */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
          Назначить клиента администратору *
        </label>
        <select
          value={assignAdminId}
          onChange={(e) => setAssignAdminId(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px'
          }}
        >
          <option value="">Выберите администратора</option>
          {admins.map(admin => (
            <option key={admin.id} value={admin.id}>
              {admin.name || admin.email}
            </option>
          ))}
        </select>
      </div>

      {/* Warning */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px',
        backgroundColor: `${COLORS.primary}10`,
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <AlertCircle size={18} color={COLORS.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
          После назначения этот клиент больше не будет отображаться в вашей панели.
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Отмена
        </button>
        <button
          onClick={onConfirm}
          disabled={!assignAdminId || processing}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: !assignAdminId || processing ? COLORS.border : COLORS.success,
            border: 'none',
            borderRadius: '8px',
            color: !assignAdminId || processing ? COLORS.textSecondary : '#fff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: !assignAdminId || processing ? 'not-allowed' : 'pointer'
          }}
        >
          {processing ? 'Обработка...' : isDeposit ? 'Подтвердить депозит' : 'Назначить'}
        </button>
      </div>
    </div>
  </div>
);

export default SupportDashboard;
